import { memo, Suspense, useEffect, useState, useTransition } from "react";
import { BsPatchQuestionFill } from "react-icons/bs";
import { useLocation, useNavigate } from "react-router-dom";
import { WEB_ROUTES } from "../../constants";
import { useAuth } from "../../context/AuthContext";
import Navbar from "./Navbar";
import styles from "./styles/ProtectedLayout.module.scss";

// Icons
import { BiSolidRename } from "react-icons/bi";
import { FaUser } from "react-icons/fa";
import { IoNotifications } from "react-icons/io5";
import {
  MdAccessTimeFilled,
  MdDashboard,
  MdOutlineMiscellaneousServices,
  MdPolicy,
} from "react-icons/md";
import { RiAdvertisementFill, RiPoliceBadgeFill } from "react-icons/ri";
// import { useLogoutUserMutation } from "../../Data/services/userAuthApi";
import Toast from "../../Hooks/useToast";
import LogoutModal from "../Modals/LogoutModal";
import TransitionLink from "../Routes/TransitionLink";
import TopProgressBar from "../TopProgressBar/TopProgressBar.js";
import { TbDeviceMobileQuestion, TbTournament } from "react-icons/tb";
import { FaProductHunt } from "react-icons/fa6";
import { logoutService } from "../../Services/logoutService.js";

const ProtectedLayout = ({ isSearchBar, isSettingButton, children }) => {
  const [collapsed, setCollapsed] = useState(window.innerWidth > 991);
  const location = useLocation();
  const [logoutModal, setLogoutModal] = useState(false);
  const [isPending, startTransition] = useTransition();
  const { logout } = useAuth();

  const isActiveRoute = (route) => {
    const regex = new RegExp(`^${route}`);
    return regex.test(location.pathname);
  };

  const navigate = useNavigate();

  const items = [
    {
      label: "Dashboard",
      icon: <MdDashboard />,
      route: WEB_ROUTES.DASHBOARD,
    },
    {
      label: "User Management",
      icon: <FaUser />,
      route: WEB_ROUTES.USER_MANAGEMENT,
      lineBreak: true,
    },
    {
      label: "Categories Management",
      icon: <MdOutlineMiscellaneousServices />,
      route: WEB_ROUTES.CATEGORY_MANAGEMENT,
    },
    {
      label: "Mental Health Management",
      icon: <BiSolidRename />,
      route: WEB_ROUTES.MENTAL_HEALTH_MANAGEMENT,
    },
    {
      label: "Dietary Restrictions",
      icon: <RiPoliceBadgeFill />,
      route: WEB_ROUTES.DIETARY_RESTRICTIONS,
      lineBreak: true,
    },
    {
      label: "Activity Management",
      icon: <TbTournament />,
      route: WEB_ROUTES.ACTIVITY_MANAGEMENT,
    },
    {
      label: "Questionnaires",
      icon: <BsPatchQuestionFill />,
      route: WEB_ROUTES.QUESTION_MANAGEMENT,
      lineBreak: true,
    },
    // {
    //   label: "Tournament",
    //   icon: <TbTournament />,
    //   route: WEB_ROUTES.TOURNAMENT_MANAGEMENT,
    // },
    // {
    //   label: "Tournament Question",
    //   icon: <TbDeviceMobileQuestion />,
    //   route: WEB_ROUTES.TOURNAMENT_QUESTION_MANAGEMENT,
    //   lineBreak: true,
    // },
    // {
    //   label: "Product", // implemented but not added in sidebar
    //   icon: <FaProductHunt />,
    //   route: WEB_ROUTES.PRODUCT_MANAGEMENT,
    //   lineBreak: true,
    // },

    {
      label: "FAQ Management",
      icon: <MdPolicy />,
      route: WEB_ROUTES.FAQ_MANAGEMENT,
    },
    {
      label: "Send Notification",
      icon: <IoNotifications />,
      route: WEB_ROUTES.SEND_NOTIFICATION,
    },
    {
      label: "Content Management",
      icon: <MdPolicy />,
      route: WEB_ROUTES.PAGE_MANAGEMENT,
    },
  ];

  const handleSettings = () => {
    startTransition(() => {
      navigate(WEB_ROUTES.SETTING);
    });
  };

  const handleToggle = () => {
    setCollapsed(!collapsed);
  };

  const handleMenuClose = () => {
    setCollapsed(false);
  };

  const handleLogoutModalOpen = () => {
    setLogoutModal(true);
  };

  const handleLogoutModalClose = () => {
    setLogoutModal(false);
  };

  const { mutate: logoutUser, isPending: isLoading } = logoutService.useCreate();

  const handleLogout = () => {
    logoutUser(null, {
      onSuccess: () => {
        Toast("Logged Out Successfully", "success");
        logout();
      },
      onError: () => {
        Toast(
          "An error occurred during log out. Logging out client-side.",
          "error"
        );
        logout(); // Log out on the client even if the server call fails
      },
    });
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 991) {
        setCollapsed(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className={styles.ProtectedLayout}>
      <div
        className={collapsed ? styles.background : ""}
        onClick={handleMenuClose}
      ></div>
      <div
        className={`${styles.menu} ${
          collapsed ? `${styles.collapsed} visible` : styles.notCollapsed
        }`}
        id="sider"
      >
        <TransitionLink
          to={`${process.env.REACT_APP_DOMAIN_DIR}/dashboard`}
          className={`${styles.logo} ${!collapsed ? styles.notCollapsed : ""} `}
        >
          <img
            src={collapsed ? "/images/logo_name.png" : "/images/small_logo.png"} //todo:
            alt="Logo"
            className={`${styles.imageNotCollapsed} image-fluid object-fit-contain`}
            width={"100%"}
            height={"100%"}
          />
        </TransitionLink>
        {items.map(({ icon, label, route, lineBreak }) => (
          <div key={route}>
            <TransitionLink
              to={route}
              className={`${styles.item} ${
                isActiveRoute(route) ? styles.active : ""
              } ${!collapsed ? styles.collapsed : ""}`}
            >
              <span style={{ fontSize: "20px", display: "flex" }}>{icon}</span>
              {collapsed ? <p>{label}</p> : ""}
            </TransitionLink>
            {lineBreak && <hr className="w-100 m-0"></hr>}
          </div>
        ))}
      </div>
      <div className={styles.routeContent}>
        {logoutModal && (
          <LogoutModal
            handleLogoutModalClose={handleLogoutModalClose}
            data={null}
            loading={isLoading}
            onLogout={handleLogout}
          />
        )}
        <Navbar
          isSearchBar={isSearchBar}
          isSettingButton={isSettingButton}
          handleToggle={handleToggle}
          handleLogoutModalOpen={handleLogoutModalOpen}
          handleSettings={handleSettings}
        />
        <div className={styles.content}>
          <Suspense fallback={<TopProgressBar />}>{children}</Suspense>
        </div>
      </div>
    </div>
  );
};

export default memo(ProtectedLayout);
